import { useState } from "react";
import { useForm } from "@mantine/form";
import {
  Button,
  Center,
  Group,
  Paper,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { api, ApiError, type CurrentUser } from "../api/client";
import { AppLogo } from "../components/AppLogo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

interface LoginProps {
  onLogin: (user: CurrentUser) => void;
}

export function Login({ onLogin }: LoginProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm({
    initialValues: { username: "", password: "" },
    validate: {
      username: (v) => (v.trim().length === 0 ? t("login.usernameRequired") : null),
      password: (v) => (v.length === 0 ? t("login.passwordRequired") : null),
    },
  });

  async function handleSubmit(values: { username: string; password: string }) {
    setSubmitting(true);
    try {
      const user = await api.login(values.username, values.password);
      onLogin(user);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      notifications.show({
        color: "red",
        title: t("login.failedTitle"),
        message: status === 401 ? t("login.badCredentials") : t("login.serverError"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh">
      <Paper withBorder shadow="md" p="xl" radius="md" w={380} maw="calc(100vw - 16px)">
        <Center mb="md">
          <AppLogo size="lg" />
        </Center>
        <Group justify="space-between" mb="md">
          <Title order={3}>{t("app.title")}</Title>
          <LanguageSwitcher />
        </Group>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label={t("login.usernameLabel")}
              autoFocus
              {...form.getInputProps("username")}
            />
            <PasswordInput
              label={t("login.passwordLabel")}
              {...form.getInputProps("password")}
            />
            <Button type="submit" loading={submitting} fullWidth>
              {t("login.submit")}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Center>
  );
}
